/**
 * Step 3: Get your tracking code.
 *
 * Bare-bones C.65 ceremony — the conversion action has been created in
 * Google Ads. Show the user the gtag snippet so they can install it. No
 * GTM walkthrough, no platform-specific guides, no install-method choice.
 * Users follow Google's official setup guide for whatever install path
 * their site uses (raw HTML, GTM, CMS plugin — not our problem).
 *
 * Expectation-setting copy mentions that Google Ads may need additional
 * setup before conversions count — managing expectations re: the known
 * post-create linkage issue without committing to fix it in this PR.
 */
import React, { useState } from 'react';
import { TagSnippet } from '../types';

const sanitizeAlnum = (value: string): string => {
  // Defense-in-depth against compromised API responses leaking unsafe
  // characters into our copy buttons. The real IDs are alnum + dashes only.
  return value.replace(/[^A-Za-z0-9_-]/g, '');
};

interface InstallationStepProps {
  conversionIdWithPrefix: string;  // e.g. 'AW-5543200460'
  conversionLabel: string;
  tagSnippets: TagSnippet[];
  conversionName: string;
}

const CopyableField: React.FC<{
  label: string;
  value: string;
  helper?: string;
}> = ({ label, value, helper }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent failure — clipboard API not available; user can select + copy manually.
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 font-mono break-all">
        {value}
      </code>
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );
};

const CopyableCodeBlock: React.FC<{
  label: string;
  code: string;
  helper: string;
}> = ({ label, code, helper }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent failure.
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
        {code}
      </pre>
      <p className="mt-1 text-xs text-gray-500">{helper}</p>
    </div>
  );
};

// Fallback snippet generators — only used when the backend doesn't return
// tag_snippets (Google Ads' API doesn't contractually guarantee them).
// Built from the sanitized ID + label so the UI never shows blank code blocks.
const buildGlobalSiteTagFallback = (safeId: string): string =>
  `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${safeId}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${safeId}');
</script>`;

const buildEventSnippetFallback = (
  safeId: string,
  safeLabel: string,
  conversionName: string,
): string =>
  `<!-- Event snippet for ${conversionName} conversion page -->
<script>
  gtag('event', 'conversion', {'send_to': '${safeId}/${safeLabel}'});
</script>`;

export const InstallationStep: React.FC<InstallationStepProps> = ({
  conversionIdWithPrefix,
  conversionLabel,
  tagSnippets,
  conversionName,
}) => {
  const safeId = sanitizeAlnum(conversionIdWithPrefix);
  const safeLabel = sanitizeAlnum(conversionLabel);
  const globalSiteTag =
    tagSnippets[0]?.global_site_tag || buildGlobalSiteTagFallback(safeId);
  const eventSnippet =
    tagSnippets[0]?.event_snippet ||
    buildEventSnippetFallback(safeId, safeLabel, conversionName);

  return (
    <div className="space-y-6">
      {/* Success banner */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="font-medium text-green-900">
              Conversion action created
            </p>
            <p className="text-sm text-green-800 mt-1">
              <strong>{conversionName}</strong> is now in your Google Ads account.
            </p>
          </div>
        </div>
      </div>

      {/* IDs */}
      <div className="space-y-3">
        <CopyableField
          label="Conversion ID"
          value={safeId}
          helper="The AW-prefixed identifier for your Google Ads account."
        />
        <CopyableField
          label="Conversion Label"
          value={safeLabel}
          helper="The per-conversion-action label that distinguishes this conversion."
        />
      </div>

      {/* Snippets */}
      <div className="space-y-4">
        <CopyableCodeBlock
          label="Global site tag"
          code={globalSiteTag}
          helper="Add this in the <head> of every page on your website. Only needed once per site."
        />
        <CopyableCodeBlock
          label="Event snippet"
          code={eventSnippet}
          helper="Fire this when the conversion happens (e.g., on a thank-you page, or on a button click)."
        />
      </div>

      {/* Installation guidance + expectation setting */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
        <p className="font-medium mb-1">Installing the tag</p>
        <p>
          See Google's{' '}
          <a
            href="https://support.google.com/google-ads/answer/6095821"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            official conversion-tag setup guide
          </a>{' '}
          for instructions on installing the snippets — raw HTML, Google Tag
          Manager, or a CMS plugin all work.
        </p>
        <p className="mt-2">
          <strong>Test on your live site, not localhost or staging.</strong>{' '}
          Google's spam filters discard conversion pings from non-public
          origins, even when the network request returns success. Fires from{' '}
          <code className="bg-blue-100 px-1 rounded">localhost</code> or
          internal preview URLs typically won't show up in Google Ads reports.
        </p>
        <p className="mt-2">
          It can take several hours for Google Ads to verify a new conversion
          action and start recording conversions, even after the tag is
          correctly installed. If conversions aren't counting after 24 hours,
          check the action's diagnostics in Google Ads.
        </p>
      </div>
    </div>
  );
};

export default InstallationStep;
